/**
 * cropRecommendationSlice.ts
 * 
 * Redux slice for managing crop recommendation state
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../index';
import { CropRecommendationOptions, CropRecommendationResult } from '../../types/cropRecommendation';
import axios from 'axios';

interface CropTypesResponse {
  cropTypes: string[];
  specificCrops: Record<string, string[]>;
  growthStages: Record<string, string[]>;
}

interface CropRecommendationState {
  data: CropRecommendationResult | null;
  availableCropTypes: {
    cropTypes: string[];
    specificCrops: Record<string, string[]>;
    growthStages: Record<string, string[]>;
  };
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

const initialState: CropRecommendationState = {
  data: null,
  availableCropTypes: {
    cropTypes: [],
    specificCrops: {},
    growthStages: {}
  },
  status: 'idle',
  error: null
};

export const fetchAvailableCropTypes = createAsyncThunk(
  'cropRecommendation/fetchAvailableCropTypes',
  async () => {
    const response = await axios.get<CropTypesResponse>('/api/crops/types');
    return response.data;
  }
);

export const generateCropRecommendation = createAsyncThunk(
  'cropRecommendation/generateRecommendation',
  async (options: CropRecommendationOptions) => {
    const response = await axios.post<CropRecommendationResult>(
      '/api/crops/recommendations',
      options
    );
    return response.data;
  }
);

const cropRecommendationSlice = createSlice({
  name: 'cropRecommendation',
  initialState,
  reducers: {
    clearRecommendations: (state) => {
      state.data = null;
      state.error = null;
      state.status = 'idle';
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAvailableCropTypes.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchAvailableCropTypes.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.availableCropTypes = action.payload;
      })
      .addCase(fetchAvailableCropTypes.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Failed to fetch crop types';
      })
      .addCase(generateCropRecommendation.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(generateCropRecommendation.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.data = action.payload;
      })
      .addCase(generateCropRecommendation.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Failed to generate recommendations';
      });
  }
});

export const { clearRecommendations } = cropRecommendationSlice.actions;

export const selectCropRecommendationData = (state: RootState) => state.cropRecommendation.data;
export const selectCropRecommendationStatus = (state: RootState) => state.cropRecommendation.status;
export const selectCropRecommendationError = (state: RootState) => state.cropRecommendation.error;
export const selectAvailableCropTypes = (state: RootState) => state.cropRecommendation.availableCropTypes;

export default cropRecommendationSlice.reducer;
